import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Checkbox, DatePicker, Select, message } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/en";
import "dayjs/locale/zh-cn";

dayjs.extend(customParseFormat);
import {
  CheckCircleOutlined,
  DownloadOutlined,
  FilterOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useUiState } from "../../state";
import { textMap } from "../../i18n";
import {
  fetchStudioSeriesList,
  type StudioSeriesDto,
} from "../../services/compliance";
import { downloadJson } from "../../utils/exportFile";
import type { ScanResultItem } from "../../types";
import type { StoredStudioScan } from "./studioScanHistoryStorage";
import { listScansInSeries } from "./studioSeries";
import { fetchStudioSeriesTasks } from "../../services/compliance";
import {
  countHighMedium,
  diffScanResponses,
} from "./studioScanCompare";
import "../../styles/studio-compare.css";
import { formatUiDateLong, formatUiDateTimeFull } from "../../utils/uiDateFormat";

type T = (typeof textMap)["en"];

/** 与 .studio-cmp-title 一致，避免标题中英混排时西文字体缺 CJK 字形 */
const CMP_HEADLINE_FONT =
  'Manrope, Inter, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif';

function cmpTpl(s: string, vars: Record<string, string | number>): string {
  let r = s;
  for (const [k, v] of Object.entries(vars)) {
    r = r.split(`{${k}}`).join(String(v));
  }
  return r;
}

type RiskFilter = "all" | "high" | "medium" | "low";

function tierFromScore(score: number): "critical" | "high" | "medium" | "low" {
  const s = Math.max(0, Math.min(100, score));
  if (s < 30) return "critical";
  if (s < 55) return "high";
  if (s < 75) return "medium";
  return "low";
}

function timelineScoreCaption(t: T, score: number): string {
  const tier = tierFromScore(score);
  const label =
    tier === "critical" || tier === "high"
      ? t.studioRecordsTagHighRisk
      : tier === "medium"
        ? t.studioRecordsTagMedium
        : t.studioRecordsTagSecure;
  return `${score}/100 · ${label}`;
}

function rowMatchesRisk(row: StoredStudioScan, f: RiskFilter): boolean {
  if (f === "all") return true;
  const t = tierFromScore(row.result.score);
  if (f === "high") return t === "critical" || t === "high";
  if (f === "medium") return t === "medium";
  return t === "low";
}

export function StudioComparePage() {
  const { lang } = useUiState();
  const t = textMap[lang];
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [seriesOptions, setSeriesOptions] = useState<StudioSeriesDto[]>([]);
  const [taskVersions, setTaskVersions] = useState<
    Map<string, number>
  >(new Map());
  const [riskFilter, setRiskFilter] = useState<RiskFilter>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [compareIds, setCompareIds] = useState<string[]>([]);

  const selectedSeriesId = searchParams.get("series") ?? "";

  const loadSeries = useCallback(async () => {
    try {
      const list = await fetchStudioSeriesList();
      setSeriesOptions(list);
    } catch {
      setSeriesOptions([]);
      message.error(t.studioSeriesLoadFailed);
    }
  }, [t]);

  useEffect(() => {
    void loadSeries();
  }, [loadSeries]);

  useEffect(() => {
    dayjs.locale(lang === "zh" ? "zh-cn" : "en"); // 显式 en，避免从中文切回英文仍用中文月份
  }, [lang]);

  useEffect(() => {
    if (!selectedSeriesId) {
      setTaskVersions(new Map());
      return;
    }
    let c = false;
    void fetchStudioSeriesTasks(selectedSeriesId)
      .then((rows) => {
        if (c) return;
        const m = new Map<string, number>();
        for (const r of rows) {
          if (r.version_no != null && r.version_no > 0) {
            m.set(r.task_id, r.version_no);
          }
        }
        setTaskVersions(m);
      })
      .catch(() => {
        if (!c) setTaskVersions(new Map());
      });
    return () => {
      c = true;
    };
  }, [selectedSeriesId]);

  const seriesRows = useMemo(() => {
    if (!selectedSeriesId) return [];
    return listScansInSeries(selectedSeriesId);
  }, [selectedSeriesId]);

  const filteredRows = useMemo(() => {
    return seriesRows.filter((row) => {
      if (!rowMatchesRisk(row, riskFilter)) return false;
      const fromOk = !dateFrom || row.completedAt >= new Date(dateFrom).getTime();
      const toDay = dateTo ? new Date(dateTo) : null;
      const toOk =
        !toDay ||
        row.completedAt <= toDay.getTime() + 24 * 60 * 60 * 1000 - 1;
      return fromOk && toOk;
    });
  }, [seriesRows, riskFilter, dateFrom, dateTo]);

  const sortedAsc = useMemo(
    () => [...filteredRows].sort((a, b) => a.completedAt - b.completedAt),
    [filteredRows],
  );

  const versionLabel = useCallback(
    (row: StoredStudioScan, indexInAsc: number) => {
      if (row.taskId && taskVersions.has(row.taskId)) {
        return `v${taskVersions.get(row.taskId)}`;
      }
      return `v${indexInAsc + 1}`;
    },
    [taskVersions],
  );

  const indexInAsc = useMemo(() => {
    const m = new Map<string, number>();
    sortedAsc.forEach((r, i) => m.set(r.scanDisplayId, i));
    return m;
  }, [sortedAsc]);

  const compareScanKey = useMemo(
    () => sortedAsc.map((r) => r.scanDisplayId).join("|"),
    [sortedAsc],
  );

  useEffect(() => {
    if (!selectedSeriesId) return;
    if (sortedAsc.length < 2) {
      setCompareIds((prev) =>
        prev.filter((id) => sortedAsc.some((r) => r.scanDisplayId === id)),
      );
      return;
    }
    setCompareIds((prev) => {
      const idSet = new Set(sortedAsc.map((r) => r.scanDisplayId));
      const valid = prev.filter((id) => idSet.has(id));
      if (valid.length === 2) return valid;
      if (valid.length === 1) return valid;
      return [
        sortedAsc[0]!.scanDisplayId,
        sortedAsc[sortedAsc.length - 1]!.scanDisplayId,
      ];
    });
  }, [selectedSeriesId, compareScanKey]); // sortedAsc：以 compareScanKey 为准，避免依赖引用抖动

  const selectedScans = useMemo(() => {
    const picked = compareIds
      .map((id) => seriesRows.find((r) => r.scanDisplayId === id))
      .filter((x): x is StoredStudioScan => Boolean(x));
    picked.sort((a, b) => a.completedAt - b.completedAt);
    return picked;
  }, [compareIds, seriesRows]);

  const older = selectedScans[0];
  const newer = selectedScans[1];
  const diff =
    older && newer
      ? diffScanResponses(older.result, newer.result)
      : null;

  const growthPct =
    older && newer && older.result.score > 0
      ? Math.round(
          ((newer.result.score - older.result.score) /
            Math.max(1, older.result.score)) *
            100,
        )
      : null;

  const setSeriesId = (id: string) => {
    setCompareIds([]);
    if (!id) {
      searchParams.delete("series");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    setSearchParams({ series: id }, { replace: true });
  };

  const toggleCompare = (scanDisplayId: string) => {
    setCompareIds((prev) => {
      if (prev.includes(scanDisplayId)) {
        return prev.filter((x) => x !== scanDisplayId);
      }
      if (prev.length < 2) {
        return [...prev, scanDisplayId];
      }
      return [prev[1]!, scanDisplayId];
    });
  };

  const exportDelta = () => {
    if (!older || !newer || !diff || !selectedSeriesId) return;
    downloadJson(
      `xtool-compare-delta-${Date.now()}.json`,
      {
        exportedAt: new Date().toISOString(),
        series_id: selectedSeriesId,
        older_scan_id: older.scanDisplayId,
        newer_scan_id: newer.scanDisplayId,
        scores: {
          older: older.result.score,
          newer: newer.result.score,
        },
        diff: {
          resolved: diff.resolved,
          new_risks: diff.newRisks,
          still_present: diff.stillPresent,
        },
      },
    );
    message.success(t.studioCompareExportJsonOk);
  };

  const seriesTitle =
    seriesOptions.find((s) => s.id === selectedSeriesId)?.title ?? "";

  return (
    <div className="studio-cmp-page" lang={lang}>
      <div className="studio-cmp-head">
        <div>
          <h1 className="studio-cmp-title">{t.studioCompareTitle}</h1>
          <p className="studio-cmp-sub">
            {seriesTitle
              ? cmpTpl(t.studioCompareSubtitleSeriesTpl, { series: seriesTitle })
              : t.studioCompareSubtitleNone}
          </p>
        </div>
        <div className="studio-cmp-filters">
          <div className="studio-cmp-filter-field">
            <label>{t.studioCompareDateFrom}</label>
            <DatePicker
              size="small"
              className="studio-cmp-filter-datepicker"
              allowClear
              value={
                dateFrom
                  ? dayjs(dateFrom, "YYYY-MM-DD", true)
                  : null
              }
              onChange={(d) =>
                setDateFrom(d && d.isValid() ? d.format("YYYY-MM-DD") : "")
              }
              format={lang === "zh" ? "YYYY年M月D日" : "MMM D, YYYY"}
              placeholder={t.studioCompareDateFrom}
            />
          </div>
          <div className="studio-cmp-filter-field">
            <label>{t.studioCompareDateTo}</label>
            <DatePicker
              size="small"
              className="studio-cmp-filter-datepicker"
              allowClear
              value={
                dateTo ? dayjs(dateTo, "YYYY-MM-DD", true) : null
              }
              onChange={(d) =>
                setDateTo(d && d.isValid() ? d.format("YYYY-MM-DD") : "")
              }
              format={lang === "zh" ? "YYYY年M月D日" : "MMM D, YYYY"}
              placeholder={t.studioCompareDateTo}
            />
          </div>
          <div className="studio-cmp-filter-field">
            <label>{t.studioCompareRisk}</label>
            <Select<RiskFilter>
              size="small"
              value={riskFilter}
              style={{ minWidth: 130 }}
              options={[
                { value: "all", label: t.studioCompareRiskAll },
                { value: "high", label: t.studioCompareRiskHigh },
                { value: "medium", label: t.studioCompareRiskMed },
                { value: "low", label: t.studioCompareRiskLow },
              ]}
              onChange={setRiskFilter}
            />
          </div>
          <div className="studio-cmp-filter-field">
            <label>{t.studioCompareSeries}</label>
            <Select<string>
              allowClear
              placeholder={t.studioCompareSeriesPh}
              value={selectedSeriesId || undefined}
              style={{ minWidth: 200 }}
              options={seriesOptions.map((s) => ({
                value: s.id,
                label: s.title,
              }))}
              onChange={(v) => setSeriesId(v ?? "")}
            />
          </div>
          <Button type="primary" icon={<FilterOutlined />}>
            {t.studioCompareApply}
          </Button>
        </div>
      </div>

      {!selectedSeriesId ? (
        <div className="studio-cmp-empty">
          <p>{t.studioComparePickSeries}</p>
          <Button type="link" onClick={() => navigate("/studio/new")}>
            {t.studioNavNewScan}
          </Button>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="studio-cmp-empty">
          <p>{t.studioCompareNoRows}</p>
        </div>
      ) : (
        <>
          <div className="studio-cmp-grid">
            <div className="studio-cmp-bento">
              <div className="studio-cmp-panel">
                <div className="studio-cmp-panel-h">
                  <div>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        color: "var(--wb-secondary)",
                        letterSpacing: "0.08em",
                      }}
                    >
                      {t.studioCompareVersionA}
                    </span>
                    <h3
                      style={{
                        margin: "4px 0 0",
                        fontSize: 18,
                        fontWeight: 800,
                        fontFamily: CMP_HEADLINE_FONT,
                      }}
                    >
                      {older
                        ? `${versionLabel(older, indexInAsc.get(older.scanDisplayId) ?? 0)} · ${formatUiDateLong(older.completedAt, lang)}`
                        : t.studioComparePickOlder}
                    </h3>
                  </div>
                  {older ? (
                    <div style={{ textAlign: "right" }}>
                      <div
                        className="studio-cmp-score"
                        style={{ color: "var(--wb-on-surface)" }}
                      >
                        {older.result.score}
                        <span style={{ fontSize: 18, opacity: 0.4 }}>/100</span>
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "var(--wb-secondary)",
                          textTransform: "uppercase",
                        }}
                      >
                        {t.studioCompareRiskScore}
                      </div>
                    </div>
                  ) : null}
                </div>
                {older ? (
                  <>
                    <div
                      style={{
                        padding: "12px 14px",
                        borderRadius: 12,
                        marginBottom: 16,
                        background: "var(--cmp-error-soft)",
                        borderLeft: `4px solid var(--wb-error)`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontWeight: 700, fontSize: 13 }}>
                        {cmpTpl(t.studioCompareFlaggedCountTpl, {
                          n: countHighMedium(older.result.flaggedItems ?? []),
                        })}
                      </span>
                      <WarningOutlined style={{ color: "var(--wb-error)" }} />
                    </div>
                    <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
                      {(older.result.flaggedItems ?? []).map((it, i) => (
                        <FindingCard key={`o-${i}`} item={it} />
                      ))}
                      {(older.result.flaggedItems ?? []).length === 0 ? (
                        <p className="studio-cmp-card-body">{t.studioCompareNoFindings}</p>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <p className="studio-cmp-card-body">{t.studioCompareSelectTwo}</p>
                )}
              </div>

              <div className="studio-cmp-gutter">
                <div
                  className="studio-cmp-gutter-inner"
                  style={{ minHeight: 200 }}
                >
                  <div className="studio-cmp-gutter-stat">
                    <div className="studio-cmp-stat-circle ok">
                      <CheckCircleOutlined />
                    </div>
                    <div
                      className="studio-cmp-stat-label"
                      style={{ color: "var(--cmp-tertiary)", marginTop: 8 }}
                    >
                      {cmpTpl(t.studioCompareResolvedNTpl, {
                        n: diff ? diff.resolved.length : 0,
                      })}
                    </div>
                  </div>
                  <div className="studio-cmp-gutter-connector" aria-hidden />
                  <div className="studio-cmp-gutter-stat">
                    <div className="studio-cmp-stat-circle bad">
                      <WarningOutlined />
                    </div>
                    <div
                      className="studio-cmp-stat-label"
                      style={{ color: "var(--wb-error)", marginTop: 8 }}
                    >
                      {cmpTpl(t.studioCompareNewNTpl, {
                        n: diff ? diff.newRisks.length : 0,
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="studio-cmp-panel">
                <div className="studio-cmp-panel-h">
                  {newer ? (
                    <div style={{ textAlign: "left" }}>
                      <div
                        className="studio-cmp-score"
                        style={{ color: "var(--wb-primary)" }}
                      >
                        {newer.result.score}
                        <span style={{ fontSize: 18, opacity: 0.4 }}>/100</span>
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "var(--wb-primary)",
                          textTransform: "uppercase",
                        }}
                      >
                        {t.studioCompareCurrentScore}
                      </div>
                    </div>
                  ) : null}
                  <div style={{ textAlign: "right" }}>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        color: "var(--wb-primary)",
                        letterSpacing: "0.08em",
                      }}
                    >
                      {t.studioCompareVersionB}
                    </span>
                    <h3
                      style={{
                        margin: "4px 0 0",
                        fontSize: 18,
                        fontWeight: 800,
                        fontFamily: CMP_HEADLINE_FONT,
                      }}
                    >
                      {newer
                        ? `${versionLabel(newer, indexInAsc.get(newer.scanDisplayId) ?? 0)} · ${formatUiDateLong(newer.completedAt, lang)}`
                        : t.studioComparePickNewer}
                    </h3>
                  </div>
                </div>
                {newer && diff ? (
                  <>
                    <div
                      style={{
                        padding: "12px 14px",
                        borderRadius: 12,
                        marginBottom: 16,
                        background: "var(--cmp-tertiary-soft)",
                        borderLeft: `4px solid var(--cmp-tertiary)`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontWeight: 700, fontSize: 13, color: "var(--cmp-tertiary)" }}>
                        {cmpTpl(t.studioCompareFlaggedCountBTpl, {
                          n: countHighMedium(newer.result.flaggedItems ?? []),
                        })}
                      </span>
                      <CheckCircleOutlined style={{ color: "var(--cmp-tertiary)" }} />
                    </div>
                    <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
                      {diff.newRisks.map((it, i) => (
                        <div key={`n-${i}`} className="studio-cmp-card new">
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: 8,
                            }}
                          >
                            <h4 className="studio-cmp-card-title" style={{ color: "var(--wb-primary)" }}>
                              {it.phrase.slice(0, 80)}
                              {it.phrase.length > 80 ? "…" : ""}
                            </h4>
                            <span className="studio-cmp-badge-new">
                              {t.studioCompareBadgeNew}
                            </span>
                          </div>
                          <p className="studio-cmp-card-body">{it.reason}</p>
                        </div>
                      ))}
                      {diff.resolved.map((it, i) => (
                        <div key={`f-${i}`} className="studio-cmp-card fixed">
                          <CheckCircleOutlined style={{ color: "var(--cmp-tertiary)", marginTop: 2 }} />
                          <div>
                            <h4 className="studio-cmp-card-title" style={{ color: "var(--cmp-tertiary)" }}>
                              {t.studioCompareFixedPrefix}
                              {it.phrase.slice(0, 60)}
                              {it.phrase.length > 60 ? "…" : ""}
                            </h4>
                            <p className="studio-cmp-card-body">{it.reason}</p>
                          </div>
                        </div>
                      ))}
                      {diff.newRisks.length === 0 && diff.resolved.length === 0 ? (
                        <p className="studio-cmp-card-body">{t.studioCompareNoDelta}</p>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <p className="studio-cmp-card-body">{t.studioCompareSelectTwo}</p>
                )}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div className="studio-cmp-side-card">
                <h4
                  style={{
                    margin: "0 0 16px",
                    fontSize: 13,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    fontFamily: CMP_HEADLINE_FONT,
                  }}
                >
                  {t.studioCompareScoreProgress}
                </h4>
                {older && newer ? (
                  <>
                    <div style={{ marginBottom: 20 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        <span>{t.studioCompareVersionA}</span>
                        <span>{older.result.score}%</span>
                      </div>
                      <div className="studio-cmp-bar">
                        <div
                          className="studio-cmp-bar-fill"
                          style={{
                            width: `${older.result.score}%`,
                            background: "var(--wb-secondary)",
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 12,
                          fontWeight: 700,
                          color: "var(--wb-primary)",
                        }}
                      >
                        <span>{t.studioCompareVersionB}</span>
                        <span>{newer.result.score}%</span>
                      </div>
                      <div className="studio-cmp-bar">
                        <div
                          className="studio-cmp-bar-fill"
                          style={{
                            width: `${newer.result.score}%`,
                            background: "var(--wb-primary)",
                          }}
                        />
                      </div>
                    </div>
                    {growthPct != null ? (
                      <div style={{ marginTop: 22, textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 800,
                            color: "var(--cmp-tertiary)",
                            fontFamily: CMP_HEADLINE_FONT,
                          }}
                        >
                          {growthPct >= 0 ? "+" : ""}
                          {growthPct}%
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "var(--wb-secondary)",
                            textTransform: "uppercase",
                            marginTop: 4,
                          }}
                        >
                          {t.studioCompareGrowth}
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p className="studio-cmp-card-body">{t.studioCompareSelectTwo}</p>
                )}
              </div>

              <div className="studio-cmp-insight">
                <h4
                  style={{
                    margin: "0 0 8px",
                    fontSize: 13,
                    fontWeight: 800,
                    fontFamily: CMP_HEADLINE_FONT,
                  }}
                >
                  {t.studioCompareInsight}
                </h4>
                <p>
                  {older && newer && diff
                    ? cmpTpl(t.studioCompareInsightBodyTpl, {
                        resolved: diff.resolved.length,
                        newRisks: diff.newRisks.length,
                        score: newer.result.score,
                      })
                    : t.studioCompareInsightEmpty}
                </p>
                <Button
                  size="small"
                  ghost
                  style={{
                    borderColor: "rgba(255,255,255,0.35)",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 800,
                    textTransform: "uppercase",
                  }}
                  icon={<DownloadOutlined />}
                  disabled={!older || !newer || !diff}
                  onClick={exportDelta}
                >
                  {t.studioCompareExportDelta}
                </Button>
              </div>
            </div>
          </div>

          <p className="studio-cmp-table-hint">{t.studioCompareLogsLead}</p>
          <div className="studio-cmp-table-wrap">
            <div className="studio-cmp-table-head">
              <h3
                style={{
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 800,
                  fontFamily: CMP_HEADLINE_FONT,
                }}
              >
                {t.studioCompareLogs}
              </h3>
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={() => {
                  downloadJson(`xtool-series-${selectedSeriesId}-logs.json`, {
                    series_id: selectedSeriesId,
                    scans: filteredRows,
                  });
                  message.success(t.studioCompareExportLogsOk);
                }}
              />
            </div>
            <table className="studio-cmp-table">
              <thead>
                <tr>
                  <th>{t.studioRecordsThTime}</th>
                  <th>{t.studioRecordsThProduct}</th>
                  <th>{t.studioRecordsThCompliance}</th>
                  <th>{t.studioCompareColVersion}</th>
                  <th>{t.studioCompareColCompare}</th>
                </tr>
              </thead>
              <tbody>
                {sortedAsc.map((row) => {
                  const i = indexInAsc.get(row.scanDisplayId) ?? 0;
                  const tier = tierFromScore(row.result.score);
                  const dotColor =
                    tier === "critical" || tier === "high"
                      ? "var(--wb-error)"
                      : tier === "medium"
                        ? "var(--wb-secondary)"
                        : "var(--cmp-tertiary)";
                  const riskLabel =
                    tier === "critical" || tier === "high"
                      ? t.studioRecordsTagHighRisk
                      : tier === "medium"
                        ? t.studioRecordsTagMedium
                        : t.studioRecordsTagSecure;
                  return (
                    <tr key={row.scanDisplayId}>
                      <td>
                        <div style={{ fontWeight: 700 }}>
                          {formatUiDateTimeFull(row.completedAt, lang)}
                        </div>
                      </td>
                      <td>
                        <code
                          style={{
                            fontSize: 11,
                            padding: "4px 8px",
                            borderRadius: 8,
                            background: "var(--wb-surface-low)",
                          }}
                        >
                          {(row.internalName || "—").slice(0, 32)}
                        </code>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span
                            className="studio-cmp-risk-dot"
                            style={{ background: dotColor }}
                          />
                          <span style={{ fontWeight: 600 }}>
                            {riskLabel} ({row.result.score})
                          </span>
                        </div>
                      </td>
                      <td style={{ fontWeight: 700 }}>
                        {versionLabel(row, i)}
                      </td>
                      <td>
                        <Checkbox
                          checked={compareIds.includes(row.scanDisplayId)}
                          onChange={() => toggleCompare(row.scanDisplayId)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <section className="studio-cmp-timeline">
            <h3
              style={{
                fontSize: 16,
                fontWeight: 800,
                marginBottom: 16,
                fontFamily: CMP_HEADLINE_FONT,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {t.studioCompareTimeline}
            </h3>
            <div className="studio-cmp-timeline-track">
              <div className="studio-cmp-timeline-line" />
              <div
                className="studio-cmp-timeline-fill"
                style={{
                  width: `${sortedAsc.length <= 1 ? 100 : (100 * (sortedAsc.length - 1)) / Math.max(1, sortedAsc.length)}%`,
                }}
              />
              <div className="studio-cmp-timeline-nodes">
                {sortedAsc.map((row, i) => {
                  const isLast = i === sortedAsc.length - 1;
                  const tier = tierFromScore(row.result.score);
                  const scoreColor =
                    tier === "critical" || tier === "high"
                      ? "var(--wb-error)"
                      : tier === "medium"
                        ? "var(--wb-secondary)"
                        : "var(--cmp-tertiary)";
                  return (
                    <div
                      key={row.scanDisplayId}
                      className={`studio-cmp-node${isLast ? " current" : ""}`}
                    >
                      <div
                        className={`studio-cmp-node-circle${isLast ? " current" : ""}`}
                      >
                        {versionLabel(row, i)}
                      </div>
                      <div style={{ marginTop: 12 }}>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>
                          {formatUiDateLong(row.completedAt, lang)}
                        </p>
                        <p
                          style={{
                            margin: "4px 0 0",
                            fontSize: 12,
                            fontWeight: 800,
                            color: scoreColor,
                          }}
                        >
                          {timelineScoreCaption(t, row.result.score)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div className="studio-cmp-node planned">
                  <div className="studio-cmp-node-circle">…</div>
                  <div style={{ marginTop: 12, opacity: 0.5 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>
                      {t.studioComparePlanned}
                    </p>
                    <p style={{ margin: "4px 0 0", fontSize: 11 }}>
                      {t.studioComparePlannedHint}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function FindingCard({ item }: { item: ScanResultItem }) {
  return (
    <div className="studio-cmp-card">
      <h4 className="studio-cmp-card-title">
        {item.phrase.slice(0, 100)}
        {item.phrase.length > 100 ? "…" : ""}
      </h4>
      <p className="studio-cmp-card-body">{item.reason}</p>
    </div>
  );
}
